import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

export type WorkspaceId = 'outbound' | 'inbound';

interface WorkspaceContextValue {
  workspace: WorkspaceId;
  setWorkspace: (next: WorkspaceId) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(
  undefined
);

const STORAGE_KEY = 'ui_workspace_mode';

// Feature flag: Inbound reception workspace (opt-in — product not GA yet)
export const INBOUND_ENABLED =
  (import.meta as any).env?.VITE_INBOUND_ENABLED === 'true';

/**
 * Returns a "strong" workspace based on the current path, or null if the path
 * is neutral (shared across workspaces).
 *
 * We only auto-switch workspaces when the user is clearly inside an
 * inbound- or outbound-specific route. Neutral routes (contacts, settings,
 * etc.) respect the user's last explicit choice.
 */
export const getStrongWorkspaceForPath = (
  pathname: string
): WorkspaceId | null => {
  if (pathname.startsWith('/app/inbound')) {
    return 'inbound';
  }

  // Outbound-centric routes (legacy + new names)
  // Note: /app/email-accounts and /app/email-templates are infrastructure, not workspace-specific
  if (
    pathname.startsWith('/app/outbound') ||
    pathname.startsWith('/app/ai-calling') ||
    pathname.startsWith('/app/sequences') ||
    pathname.startsWith('/app/campaign-analytics')
  ) {
    return 'outbound';
  }

  return null;
};

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();

  const [workspace, setWorkspaceState] = useState<WorkspaceId>(() => {
    let initial: WorkspaceId = 'outbound';

    // 1) Restore last explicit choice if possible
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as WorkspaceId | null;
      if (stored === 'inbound' && !INBOUND_ENABLED) {
        initial = 'outbound';
      } else if (stored === 'outbound' || stored === 'inbound') {
        initial = stored;
      }
    } catch {
      // ignore
    }

    // 2) If the current path clearly belongs to a workspace, let that win
    const strong = getStrongWorkspaceForPath(location.pathname);
    if (strong && (strong !== 'inbound' || INBOUND_ENABLED)) {
      initial = strong;
    }

    return initial;
  });

  // Keep workspace in sync when navigating into clearly inbound/outbound areas.
  useEffect(() => {
    const strong = getStrongWorkspaceForPath(location.pathname);
    if (!strong) return;
    if (strong === 'inbound' && !INBOUND_ENABLED) return;

    setWorkspaceState((prev) => (prev === strong ? prev : strong));
  }, [location.pathname]);

  const setWorkspace = (next: WorkspaceId) => {
    if (next === 'inbound' && !INBOUND_ENABLED) return;

    setWorkspaceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  };

  const value = useMemo(
    () => ({
      workspace,
      setWorkspace,
    }),
    [workspace]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = (): WorkspaceContextValue => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return ctx;
};


